import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaChartLine, FaUsers, FaLightbulb, FaGlobeAmericas } from 'react-icons/fa';
import './GeneralPageStyles.css';

const WhoWeAre = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const teamMembers = [
    {
      id: 1,
      name: "Michael Roberts",
      position: "CEO & Founder",
      bio: "Former investment banker with 15+ years in financial services and regulatory compliance.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80"
    },
    {
      id: 2,
      name: "Sarah Johnson",
      position: "Chief Technology Officer",
      bio: "Tech innovator with expertise in fintech platforms and blockchain technology.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80"
    },
    {
      id: 3,
      name: "David Chen",
      position: "Head of Research",
      bio: "PhD in Financial Economics with focus on market analysis and risk management.",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80"
    },
    {
      id: 4,
      name: "Emma Williams",
      position: "Compliance Director",
      bio: "Legal expert specializing in financial regulation across multiple jurisdictions.",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80"
    }
  ];

  const values = [
    {
      id: 1,
      icon: <FaChartLine />,
      title: "Integrity",
      description: "We operate with the highest ethical standards, providing transparent and honest information about all listed brokers."
    },
    {
      id: 2,
      icon: <FaUsers />,
      title: "Client Focus",
      description: "Our clients' success is our priority. We continuously innovate to meet their evolving needs."
    },
    {
      id: 3,
      icon: <FaLightbulb />,
      title: "Innovation",
      description: "We leverage cutting-edge technology to deliver superior services and user experiences."
    },
    {
      id: 4,
      icon: <FaGlobeAmericas />,
      title: "Global Perspective",
      description: "We understand international markets and regulatory environments to serve our diverse client base."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const floatAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Who We Are
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Meet the team behind UPM Traders
          </motion.p>
        </div>
      </div>
      
      <div className="container">
        <motion.div 
          ref={ref}
          className="intro-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="intro-text" variants={itemVariants}>
            <h2>Our Story</h2>
            <p>
              UPM Traders is a team of financial experts and technology professionals dedicated to 
              helping traders navigate the complex world of online trading. With years of experience 
              in the financial services industry, we understand the importance of finding trustworthy 
              and efficient trading platforms.
            </p>
            <p>
              Our mission is to simplify the process of finding reputable brokers by thoroughly vetting and 
              listing only those that meet our strict criteria for regulation, registration, and 
              withdrawal efficiency. Our team continuously monitors the market to ensure that all 
              information remains current and relevant.
            </p>
          </motion.div>
          
          <motion.div className="mission-statement" variants={itemVariants}>
            <h2>Our Mission</h2>
            <p>
              To empower traders worldwide with the knowledge and tools they need to make informed 
              decisions about their trading partners, ultimately leading to more successful and secure 
              trading experiences.
            </p>
          </motion.div>
        </motion.div>
        
        <motion.section 
          className="values-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.h2 
            variants={itemVariants}
          >
            Our Core Values
          </motion.h2>
          <div className="values-grid staggered-animations">
            {values.map((value, index) => (
              <motion.div 
                className="value-card" 
                key={value.id}
                variants={itemVariants}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="value-icon"
                  animate={floatAnimation}
                >
                  {value.icon}
                </motion.div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
        
        <motion.section 
          className="team-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.h2 
            variants={itemVariants}
          >
            Our Leadership Team
          </motion.h2>
          <div className="team-grid staggered-animations">
            {teamMembers.map((member, index) => (
              <motion.div 
                className="team-member" 
                key={member.id}
                variants={itemVariants}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="member-image"
                  whileHover={{ 
                    scale: 1.05,
                    transition: { duration: 0.3 }
                  }}
                >
                  <img src={member.image} alt={member.name} />
                </motion.div>
                <div className="member-info">
                  <motion.h3
                    animate={floatAnimation}
                    transition={{ delay: index * 0.1 }}
                  >
                    {member.name}
                  </motion.h3>
                  <p className="position">{member.position}</p>
                  <p className="bio">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default WhoWeAre;